import React, { useState, useEffect, useRef } from 'react';
import { Calendar as CalendarIcon, ChevronRight } from 'lucide-react';
import { addBusinessHours } from '../../utils/planningUtils';

export default function RangeDatePicker({ startDate, endDate, onChange, hours, businessHours, closures }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [show, setShow] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setShow(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const d1 = startDate ? new Date(startDate) : null;
  const d2 = endDate ? new Date(endDate) : null;

  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const startDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const days = daysInMonth(currentDate.getFullYear(), currentDate.getMonth());
  
  const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const DAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const handleDayClick = (day) => {
  const clicked = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
  const dayName = DAY_KEYS[clicked.getDay()];
  const config = businessHours?.[dayName] || { open: '08:00', close: '17:30' };
  const [oh, om] = (config.open || '08:00').split(':').map(Number);
  const [ch, cm] = (config.close || '17:30').split(':').map(Number);
  
  const h = parseFloat(hours);
  
  if (!d1 || (d1 && d2 && d1.getTime() !== d2.getTime())) {
    const start = new Date(clicked);
    start.setHours(oh);
    start.setMinutes(om);
    
    if (!isNaN(h) && h > 0 && businessHours) {
      const computedEnd = addBusinessHours(start.toISOString(), h, businessHours, closures);
      onChange(start.toISOString(), computedEnd);
    } else {
      onChange(start.toISOString(), start.toISOString()); 
    }
  } else {
    // On a déjà un d1, on fixe la fin
    if (clicked < d1) {
      const start = new Date(clicked);
      // On garde l'heure standard d'ouverture pour le départ
      start.setHours(oh); start.setMinutes(om);
      
      if (!isNaN(h) && h > 0 && businessHours) {
        const computedEnd = addBusinessHours(start.toISOString(), h, businessHours, closures);
        onChange(start.toISOString(), computedEnd);
      } else {
        onChange(start.toISOString(), start.toISOString());
      }
    } else {
      const end = new Date(clicked);
      end.setHours(ch);
      end.setMinutes(cm);
      onChange(d1.toISOString(), end.toISOString());
    }
  }
};

  const isSelected = (day) => {
    const d = new Date(currentDate.getFullYear(), currentDate.getMonth(), day, 12);
    if (!d1) return false;
    const t1 = new Date(d1); t1.setHours(12);
    if (!d2) return d.getTime() === t1.getTime();
    const t2 = new Date(d2); t2.setHours(12);
    return d >= t1 && d <= t2;
  };

  const isStart = (day) => {
    const d = new Date(currentDate.getFullYear(), currentDate.getMonth(), day, 12);
    if (!d1) return false;
    const t1 = new Date(d1); t1.setHours(12);
    return d.getTime() === t1.getTime();
  };

  const isEnd = (day) => {
    const d = new Date(currentDate.getFullYear(), currentDate.getMonth(), day, 12);
    if (!d2) return false;
    const t2 = new Date(d2); t2.setHours(12);
    return d.getTime() === t2.getTime();
  };

  const formatStr = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) + " à " + new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : "Choisir...";

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ position: 'relative' }}>
          <label style={{ display: 'block', fontSize: 9, fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4, marginLeft: 4 }}>Début</label>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <input 
              type="datetime-local"
              value={startDate ? new Date(new Date(startDate).getTime() - new Date(startDate).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
              onChange={(e) => {
                const val = e.target.value;
                if (!val) return;
                const newStart = new Date(val).toISOString();
                onChange(newStart, endDate || newStart);
              }}
              style={{ 
                width: '100%',
                background: '#f8fafc', 
                border: '1.5px solid #e2e8f0', 
                borderRadius: 14, 
                padding: '10px 12px', 
                fontSize: 13,
                fontWeight: 700,
                color: '#1e293b',
                outline: 'none',
                transition: 'all 0.2s'
              }}
            />
            <div 
              onClick={() => setShow(!show)}
              style={{ position: 'absolute', right: 10, cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center' }}
            >
              <CalendarIcon size={16} />
            </div>
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <label style={{ display: 'block', fontSize: 9, fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4, marginLeft: 4 }}>Fin</label>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <input 
              type="datetime-local"
              value={endDate ? new Date(new Date(endDate).getTime() - new Date(endDate).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
              onChange={(e) => {
                const val = e.target.value;
                if (!val) return;
                const newEnd = new Date(val).toISOString();
                onChange(startDate || newEnd, newEnd);
              }}
              style={{ 
                width: '100%',
                background: '#f8fafc', 
                border: '1.5px solid #e2e8f0', 
                borderRadius: 14, 
                padding: '10px 12px', 
                fontSize: 13,
                fontWeight: 700,
                color: '#1e293b',
                outline: 'none',
                transition: 'all 0.2s'
              }}
            />
          </div>
        </div>
      </div>

      {show && (
        <div style={{ 
          position: 'absolute', 
          top: 'calc(100% + 8px)', 
          left: 0, 
          width: '320px',
          background: '#fff', 
          border: '1px solid #e2e8f0', 
          borderRadius: 24, 
          padding: 20, 
          zIndex: 11000, 
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)' 
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
            <button type="button" onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>←</button>
            <div style={{ fontWeight: 900, fontSize: 14, color: '#0f172a' }}>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</div>
            <button type="button" onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>→</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, textAlign: 'center', marginBottom: 15 }}>
            {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map(d => <div key={d} style={{ fontSize: 10, fontWeight: 900, color: '#94a3b8', paddingBottom: 5 }}>{d}</div>)}
            {Array(startDay === 0 ? 6 : startDay - 1).fill(null).map((_, i) => <div key={'e' + i} />)}
            {Array(days).fill(0).map((_, i) => {
              const d = i + 1;
              const sel = isSelected(d);
              const start = isStart(d);
              const end = isEnd(d);
              const isFullRange = d1 && d2 && d1.toDateString() !== d2.toDateString();
              
              return (
                <div 
                  key={d} 
                  onClick={() => handleDayClick(d)} 
                  style={{ 
                    height: 36, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: 13, 
                    fontWeight: 700, 
                    cursor: 'pointer',
                    position: 'relative',
                    borderRadius: 10,
                    transition: 'all 0.2s',
                    background: (sel && isFullRange) ? '#eff6ff' : 'transparent', 
                    color: sel ? (start || end ? '#fff' : '#3b82f6') : '#475569',
                    ...(start && { background: '#0f172a', color: '#fff', borderRadius: isFullRange ? '10px 0 0 10px' : '10px' }),
                    ...(end && { background: '#0f172a', color: '#fff', borderRadius: isFullRange ? '0 10px 10px 0' : '10px' })
                  }}
                  onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = 'transparent'; }}
                >
                  {d}
                </div>
              );
            })}
          </div>

          {d1 && (
            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 15, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 9, fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Heure Début</label>
                <input 
                  type="time" 
                  value={d1.toTimeString().slice(0, 5)} 
                  onChange={e => {
                    const [h, m] = e.target.value.split(':');
                    const newD1 = new Date(d1); newD1.setHours(h); newD1.setMinutes(m);
                    onChange(newD1.toISOString(), d2 ? d2.toISOString() : newD1.toISOString());
                  }} 
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, fontWeight: 700, background: '#f8fafc', outline: 'none' }} 
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 9, fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Heure Fin</label>
                <input 
                  type="time" 
                  value={d2 ? d2.toTimeString().slice(0, 5) : '17:00'} 
                  onChange={e => {
                    const [h, m] = e.target.value.split(':');
                    const newD2 = new Date(d2 || d1); newD2.setHours(h); newD2.setMinutes(m);
                    onChange(d1.toISOString(), newD2.toISOString());
                  }} 
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, fontWeight: 700, background: '#f8fafc', outline: 'none' }} 
                />
              </div>
            </div>
          )}
          
          <button 
            type="button" 
            onClick={() => setShow(false)} 
            style={{ 
              width: '100%', 
              marginTop: 15, 
              background: '#0f172a', 
              color: '#fff', 
              border: 'none', 
              padding: '12px', 
              borderRadius: 14, 
              fontWeight: 800, 
              fontSize: 13, 
              cursor: 'pointer',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
          >
            Confirmer la période
          </button>
        </div>
      )}
    </div>
  );
}
